/**
 * A TV ao vivo: o que chega do serviço de IPTV, já em forma de tela.
 *
 * Porte de `lib/features/cinema/data/services/iptv_service.dart`. Só o que é
 * puro mora aqui — ler um canal de um JSON, dobrar acento para comparar nome,
 * separar as listas próprias das compartilhadas. Quem fala com o servidor são
 * os métodos `iptv*` do cliente, porque o prazo, o cookie e o 409 são de lá.
 *
 * Nada de credencial passa por aqui. A chave da conta vai de mão única para o
 * servidor e nunca volta; a sessão do pareamento fica lá e também não volta.
 */

/** Um canal, um filme, uma série ou uma categoria: a lista é a mesma. */
export interface EntradaIptv {
  id: string;
  nome: string;
  logo: string;
  /** `container_extension`. A URL do fluxo precisa dele para VOD e episódio. */
  ext: string;
}

/**
 * Uma entrada do serviço, ou `null` quando ela não tem sequer um id.
 *
 * Cada rota do serviço dá outro nome ao mesmo campo — canal é `stream_id`,
 * série é `series_id`, categoria é `category_id` — e a tela trata os três
 * igual, então a diferença morre aqui.
 */
export function entradaIptv(cru: unknown): EntradaIptv | null {
  if (!cru || typeof cru !== 'object') return null;
  const j = cru as Record<string, unknown>;

  const id = `${j.stream_id ?? j.series_id ?? j.category_id ?? j.id ?? ''}`;
  if (!id) return null;

  const nome = `${j.name ?? j.title ?? j.category_name ?? ''}`.trim();
  const logo = `${j.stream_icon ?? j.cover ?? j.icon ?? ''}`.trim();

  return {
    id,
    nome: nome || 'Sem nome',
    // Um caminho relativo aqui viraria uma imagem quebrada; melhor o ícone.
    logo: logo.startsWith('http') ? logo : '',
    ext: `${j.container_extension ?? ''}`.trim(),
  };
}

const COM_ACENTO = 'áàâãäéèêëíìîïóòôõöúùûüçñ';
const SEM_ACENTO = 'aaaaaeeeeiiiiooooouuuucn';

/**
 * O nome dobrado para comparar: minúsculo e sem acento.
 *
 * Quem procura "sao paulo" quer achar "São Paulo", e o teclado do celular não
 * ajuda a digitar o til.
 */
export function iptvSemAcento(texto: string): string {
  let saida = '';
  for (const letra of texto.toLowerCase()) {
    const i = COM_ACENTO.indexOf(letra);
    saida += i >= 0 ? SEM_ACENTO[i] : letra;
  }
  return saida;
}

/** Uma lista da conta: a da pessoa ou uma que alguém compartilhou com ela. */
export interface ListaIptv {
  id: string;
  nome: string;
  compartilhada: boolean;
}

/**
 * As listas de `/iptv/read?path=playlists`.
 *
 * O serviço devolve as próprias e as compartilhadas em chaves diferentes, e a
 * mesma lista pode aparecer nas duas. A ordem de leitura decide: quem já veio
 * como própria não volta como compartilhada.
 */
export function listasIptv(corpo: unknown): ListaIptv[] {
  const mapa = (corpo && typeof corpo === 'object' ? corpo : {}) as Record<string, unknown>;
  const saida: ListaIptv[] = [];
  const vistos = new Set<string>();

  const grupos: Array<[string, boolean]> = [
    ['owned', false],
    ['playlists', false],
    ['shared', true],
  ];

  for (const [chave, compartilhada] of grupos) {
    const bruto = mapa[chave];
    if (!Array.isArray(bruto)) continue;

    for (const item of bruto) {
      if (!item || typeof item !== 'object') continue;
      const j = item as Record<string, unknown>;
      const id = `${j.id ?? j.playlist_id ?? ''}`.trim();
      if (!id || vistos.has(id)) continue;
      vistos.add(id);

      const nome = `${j.name ?? j.title ?? j.label ?? ''}`.trim();
      saida.push({ id, nome: nome || `Lista ${saida.length + 1}`, compartilhada });
    }
  }

  return saida;
}

/** O endereço que toca, e se ele é HLS (canal ao vivo) ou arquivo. */
export interface FluxoIptv {
  url: string;
  hls: boolean;
}

/** O estado da conexão com o serviço, para a tela saber o que oferecer. */
export interface LigacaoIptv {
  ligada: boolean;
  /** `chave` quando é chave de conta, `pareamento` quando é sessão aprovada. */
  via: string;
  /** A chave existe mas o serviço a recusou: pedir outra, não parear de novo. */
  recusada: boolean;
}

/** O código de quatro dígitos e onde aprová-lo. */
export interface PareamentoIptv {
  codigo: string;
  aparelho: string;
  endereco: string;
}

/**
 * Extrai a lista de dentro do envelope, seja qual for o nome dele.
 *
 * Cada rota do serviço embrulha diferente, e nenhuma delas é nossa para mudar.
 */
export function itensIptv(corpo: unknown): unknown[] {
  if (Array.isArray(corpo)) return corpo;
  if (!corpo || typeof corpo !== 'object') return [];

  const mapa = corpo as Record<string, unknown>;
  for (const chave of ['data', 'items', 'streams', 'categories', 'results']) {
    if (Array.isArray(mapa[chave])) return mapa[chave] as unknown[];
  }
  return [];
}

/**
 * Os episódios de uma série, achatados na ordem das temporadas.
 *
 * O serviço devolve um mapa com a temporada na chave, e chave de JSON não tem
 * ordem: sem ordenar por número, a segunda temporada vem antes da primeira.
 */
export function episodiosIptv(corpo: unknown): EntradaIptv[] {
  const raiz = (corpo && typeof corpo === 'object' ? corpo : {}) as Record<string, unknown>;
  const dados = (
    raiz.data && typeof raiz.data === 'object' ? raiz.data : raiz
  ) as Record<string, unknown>;

  const temporadas = dados.episodes;
  if (!temporadas || typeof temporadas !== 'object') return [];

  const chaves = Object.keys(temporadas as Record<string, unknown>).sort(
    (a, b) => (Number(a) || 0) - (Number(b) || 0),
  );

  const saida: EntradaIptv[] = [];
  for (const temporada of chaves) {
    const lista = (temporadas as Record<string, unknown>)[temporada];
    if (!Array.isArray(lista)) continue;

    for (const item of lista) {
      if (!item || typeof item !== 'object') continue;
      const j = item as Record<string, unknown>;
      const id = `${j.id ?? ''}`.trim();
      if (!id) continue;

      const numero = `${j.episode_num ?? ''}`.padStart(2, '0');
      const titulo = `${j.title ?? ''}`.trim();
      const marca = `T${temporada}E${numero}`;

      saida.push({
        id,
        nome: titulo ? `${marca} · ${titulo}` : marca,
        logo: '',
        ext: `${j.container_extension ?? ''}`.trim(),
      });
    }
  }

  return saida;
}
